/**
 * Characters disallowed in filenames on common operating systems (Windows reserves the whole set;
 * `/` and `\` break paths on macOS/Linux too). Stripped — not quoted — from the campaign name: the
 * value only names a download here, so removing them is what keeps the save from failing.
 */
const FILENAME_FORBIDDEN_CHARS = /[/\\:*?"<>|]/g;

/** A whole-dollar price reads as "100"; a fractional one keeps two places ("30.50"). */
function priceLabel(pricePerGift: number): string {
  return Number.isInteger(pricePerGift) ? String(pricePerGift) : pricePerGift.toFixed(2);
}

/**
 * The identifiable filename every campaign contacts / gift-links CSV downloads as —
 * `Campaign Name - $Price.csv`, e.g. "Google Holiday Gifts - $100.csv". `pricePerGift` is the
 * collection tier the campaign was built on (its per-gift value), NOT the lowest item in the
 * collection. Disallowed filename characters are stripped from the name so the download never breaks;
 * a name left empty by that strip falls back to "Campaign".
 *
 * When the export is narrowed to a single status, pass it as `status` to append it —
 * `Campaign Name - $Price - Redeemed.csv` — so a partial file can't be mistaken for the full list.
 * Omit (or pass null) for the unfiltered export.
 *
 * The `$` here is a tier LABEL inside a filename, not a money value on screen, so the ticket's exact
 * "$100" (no cents) is intended — which is why this lives in its own file, allowlisted in the
 * currency-format audit rather than routed through `formatUsd` (which would emit "$100.00").
 */
export function campaignCsvFilename(
  campaignName: string,
  pricePerGift: number,
  status?: string | null,
): string {
  const safeName = campaignName.replace(FILENAME_FORBIDDEN_CHARS, "").trim() || "Campaign";
  const statusPart = status ? status.replace(FILENAME_FORBIDDEN_CHARS, "").trim() : "";
  const suffix = statusPart ? ` - ${statusPart}` : "";
  return `${safeName} - $${priceLabel(pricePerGift)}${suffix}.csv`;
}
